import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AutomobileService {

  /**
   * Référentiel par catégorie, mis en cache pendant REFERENTIEL_TTL_MS.
   *
   * Genre, usage, durées et options d'une catégorie ne changent pas pendant qu'on saisit
   * un contrat. En flotte, chaque bascule d'un véhicule à l'autre relançait deux appels
   * (jeton + référentiel) et laissait les listes vides le temps de la réponse : c'est ce
   * qui faisait apparaître Genre et Usage à blanc.
   *
   * `shareReplay(1)` sert la même réponse à tous les abonnés, y compris à ceux qui
   * arrivent pendant que l'appel est encore en vol — deux bascules rapprochées ne
   * déclenchent donc qu'une seule requête.
   *
   * Passé le délai, l'entrée est abandonnée et l'appel refait normalement : une
   * modification du référentiel côté ORASS est ainsi reprise sans rechargement de la page.
   */
  private static readonly REFERENTIEL_TTL_MS = 10 * 60 * 1000;

  private referentialByCategory = new Map<string, { flux: Observable<any>, expireA: number }>();

  constructor(
    private http: HttpClient
  ) { }

  // search assuré
  searchAssureByName(token: string, searchParam: string, procedureName: string) {
    return this.http.get(`${environment.assureBaseUrl}/api/Request`, {params: {
      token, 
      jsonParametre: searchParam, 
      nomProcedure: procedureName,
      identifiant: `${environment.identProcess}`
    }});
  }

  // List garanties with caracteristics
  getAllGaranties(code, token) {
    return this.http.get(`${environment.automobileBaseUrl}/api/v1/Tarification/categoriegarantiecaracteristique?codecate=${code}`, {params: {token}});
  }

  // Get referential data
  getAllDatas(token) {
    return this.http.get(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/ReferentialData`, {params: {token}});
  }

  // Get referential datas per category
  getAllDatasPerCategory(code, token): Observable<any> {
    const cle = code + '';
    const entree = this.referentialByCategory.get(cle);

    // Entrée encore valable : on la ressert sans rappeler l'API.
    if (entree && Date.now() < entree.expireA) {
      return entree.flux;
    }

    // Le jeton ne fait pas partie de la clé : il change à chaque appel alors que la
    // réponse, elle, ne dépend que de la catégorie. L'appel refait ici emporte le jeton
    // courant, valide au moment où la requête part.
    const flux = this.http
      .get(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/ReferentialDataByCategorie/${code}`, { params: { token } })
      .pipe(
        // Une erreur ne doit pas rester en cache : sans cela, une coupure réseau
        // condamnerait la catégorie jusqu'à expiration, sans possibilité de réessai.
        catchError(err => {
          this.referentialByCategory.delete(cle);
          return throwError(err);
        }),
        // Une réponse sans DureByCate (back temporairement indisponible, catégorie encore
        // non provisionnée côté référentiel...) ne doit pas non plus rester en cache : sans
        // ce retrait, le select Durée restait vide pendant 10 minutes pour toute la catégorie,
        // même en rouvrant un contrat différent.
        map((res: any) => {
          if (!res?.DureByCate || (Array.isArray(res.DureByCate) && res.DureByCate.length === 0)) {
            this.referentialByCategory.delete(cle);
          }
          return res;
        }),
        shareReplay(1)
      );

    this.referentialByCategory.set(cle, {
      flux,
      expireA: Date.now() + AutomobileService.REFERENTIEL_TTL_MS
    });

    return flux;
  }

  /** Vide le cache du référentiel (changement de point de vente, reconnexion). */
  clearReferentialCache(code: any = null): void {
    if (code === null) {
      this.referentialByCategory.clear();
    } else {
      this.referentialByCategory.delete(code + '');
    }
  }

  // Tarification per garantie
  tarificationGarantie(data: any, token) {
    return this.http.post(`${environment.automobileBaseUrl}/api/v1/Tarification/tarifgarantie`, data, {params: {token}});
  }

  // Tarification per contract
  tarificationContrat(data: any, token) {
    return this.http.post(`${environment.automobileBaseUrl}/api/v1/Tarification/tarifpolice`, data, {params: {token}});
  }

  // Tarification for flotte
  tarificationFlotte(data: any, token) {
    return this.http.post(`${environment.automobileBaseUrl}/api/v1/Tarification/tarifflotte`, data, {params: {token}});
  }

  // Commission per garantie
  commissionGarantie(data: any, token) {
    return this.http.post(`${environment.automobileBaseUrl}/api/v1/Tarification/commissiongarantie`, data, {params: {token}});
  }

  // Commission per contract
  commissionContrat(data: any, token) {
    return this.http.post(`${environment.automobileBaseUrl}/api/v1/Tarification/commissionpolice`, data, {params: {token}});
  }

  // Commission for flotte
  commissionFlotte(data: any, token) {
    return this.http.post(`${environment.automobileBaseUrl}/api/v1/Tarification/commissionflotte`, data, {params: {token}});
  }
  
  // Subscription contract
  generateBearerToken() {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/Authentication/Authenticate`, {
      "userId": `${environment.autoAuthApiUser}`,
      "password": `${environment.autoAuthApiPassword}`
    });
  }

  // Subscription contract
  generateUserBearerToken() {
    return this.http.post(`${environment.assureBaseUrl}/api/Authentication/Authenticate`, {
      "nomutil": `${environment.assureAuthApiUser}`,
      "userkey": `${environment.assureAuthApiPassword}`
    });
  }

  // Subscription contract
  generateAutoBearerToken() {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/Authentication/Authenticate`, {
      "userId": `${environment.autoAuthApiUser}`,
      "password": `${environment.autoAuthApiPassword}`
    });
  }

  // Subscription contract
  souscriptionPolice(data: any, token) {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/NewContractCMR`, data, {params: {token}});
  }

  // Save encaissement
  saveEncaissement(data: any, token) {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/EncaissementCMR/Encaissement`, data, {params: {token}});
  }

  savePolice(data: any, pos: string = null/*, token*/) {
    return this.http.post(`${environment.automobileExtraBaseUrl}/policy/save-policy`, data, {params: pos ? {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'), pointofsale: pos} : {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }

  printCarteBrune(data: any, idctr, numTypDocu, token) {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/AttestationCMR/${idctr}`, data, {params: {token, num: numTypDocu}});
  }

  // Attestations flotte : un appel pour tous les risques du contrat, réponse {idctr, total_risques, succes, message, lien_zip}
  printCarteBruneFlotte(data: any, idctr, numTypDocu, token) {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/AttestationFlotteCMR/${idctr}`, data, {params: {token, num: numTypDocu}});
  }

  // --- Attestations ASAC ---
  // Jeton ASAC : réponse {token, token_name, expires_at, application_key_name}
  generateAsacToken(token) {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/asac/generate-token`, {}, {params: {token}});
  }

  // Déclaration de la production auprès de l'ASAC (corps : AffaireNouvelleDto).
  // `token` est le jeton applicatif APIM : le serveur gère lui-même l'authentification ASAC.
  sendAsacProduction(data: any, token) {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/asac/productions`, data, {params: {token}});
  }

  // Récupération du document ASAC (corps : AffaireNouvelleDto), même jeton applicatif.
  downloadAsacProduction(data: any, token) {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/asac/productions/download`, data, {params: {token}});
  }

  printCP(data: any, token) {
    return this.http.post(`${environment.printingBaseUrl}/api/Report/GetPdfReportCMR`, data, {params: {token}});
  }

  printReport(data: any, type, token) {
    return this.http.post(`${environment.printingBaseUrl}/api/Report/GetPdfReportCMR/${type}`, data, {params: {token}});
  }

  printAttestation(idctr: string, data:any, token) {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/EditerAttestation_v2`, data, {params: {idctr, token}});
  }

  // avenantNumber facultatif : sans lui, l'API renvoie l'état courant de la police (dernier avenant validé).
  getSavedContrat(numPolice: string, pos: string = null/*, token*/, avenantNumber: string = null) {
    const params: any = pos ? {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'), pointofsale: pos} : {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')};
    if (avenantNumber !== null && avenantNumber !== undefined && (avenantNumber + '').trim() !== '') {
      params.avenantnumber = (avenantNumber + '').trim();
    }
    return this.http.get(`${environment.automobileExtraBaseUrl}/policy/get/${numPolice}`, {params});
  }

  getNumeroPolice(/*token*/) {
    return this.http.get(`${environment.automobileExtraBaseUrl}/policy/generate`, {params: {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }

  getAttestationNumbers(posId/*token*/) {
    return this.http.get(`${environment.automobileExtraBaseUrl}/attestations/available/${posId}`, {params: {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }

  checkPrintPremission(numPolice: string, data/*token*/) {
    return this.http.post(`${environment.automobileExtraBaseUrl}/attestations/check-print/${numPolice}`, {params: {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }

  sendPrintStatus(data: any/*token*/) {
    return this.http.post(`${environment.automobileExtraBaseUrl}/attestations/save-policy`, data, {params: {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }

  sendContractSubmitConfirm(numPolice: string/*token*/) {
    return this.http.get(`${environment.automobileExtraBaseUrl}/attestations/check-print/${numPolice}`, {params: {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }

  updateContractWithCardNumber(idctr: string, typeDoc, data: any, token) {
    return this.http.put(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/SetNumeroCarte/${idctr}/${typeDoc}`, data, {params: {token}});
  }

  getConditionFields(codecate/*token*/) {
    return this.http.get(`${environment.automobileExtraBaseUrl}/produit/listparmasreglesouscription/${codecate}`, {params: {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }

  checkParticularConditions(codecate: string, data/*token*/) {
    return this.http.post(`${environment.automobileExtraBaseUrl}/produit/checkRules/${codecate}`, data, {params: {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }


  //renewals
  renouvellementPolice(data: any, token) {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/RenouvellementCMR/RenewContractCMR`, data, {params: {token}});
  }

  //contract differential
  getDifferentielPolice(data: any, token) {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/DifferrentialContract`, data, {params: {token}});
  }

  // get types avenants
  getTypesAvenants(token: string) {
    return this.http.get(`${environment.assureBaseUrl}/api/Request`, {params: {
      token, 
      jsonParametre: "{}", 
      nomProcedure: "GetTypeAvenant",
      identifiant: `${environment.identProcess}`
    }});
  }

  //get garanties and dependances
  getProductGarantiesConditions(codecate: string) {
    return this.http.get(`${environment.automobileExtraBaseUrl}/produit/getallgarantiedeps/${codecate}`, {params: {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }

  // recherche historique validations
  getValidationsHistory(data, token) {
    return this.http.get(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/GetBordereauProd/${data.codeunite}/${data.date_debut}/${data.date_fin}?codecate=${data.categorie}`, {params: {token}});
  }

  // recherche historique validations
  getPropositionsHistory(data) {
    return this.http.get(`${environment.automobileExtraBaseUrl}/policy/list-proposition`, {
      params: {
        token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'),
        dateDebut: data.date_debut,
        dateFin: data.date_fin,
        categorie: data.categorie
      }
    });
  }

  // search polices for avenant
  searchPolicies(data) {
    return this.http.get(`${environment.automobileExtraBaseUrl}/policy/search-policies`, {
      params: {
        token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'),
        numeroPolice: data.num_police,
        nomSouscripteur: data.nom_souscript,
        immatriculation: data.immatriculation
      }
    });
  }

  // search polices from contrat for avenant
  searchPoliciesFromContrat(data: any, token) {
    return this.http.post(`${environment.subscriptionBaseUrl}/api/v1/SouscriptionCMR/RechercheContratCIV_v2`, null, {
      params: {
        token: token,
        numepolice: data.num_police,
        nomclient: data.nom_souscript,
        numimma: data.immatriculation
      }
    });
  }

  
  // synchronise contract
  syncPolice(data: any, pos: string = null/*, token*/) {
    return this.http.post(`${environment.automobileExtraBaseUrl}/policy/sync-policy`, data, {params: pos ? {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'), pointofsale: pos} : {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }

  //update police from MS contrat to showroom
  updatePolice(data: any, pos: string = null) {
    return this.http.post(`${environment.automobileExtraBaseUrl}/avenant/save-avenant`, data, {params: pos ? {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'), pointofsale: pos} : {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }

  // search saved avenants
  searchSavedAvenants(data) {
    return this.http.get(`${environment.automobileExtraBaseUrl}/avenant/search-avenants`, {
      params: {
        token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'),
        numeroPolice: data.num_police,
        nomSouscripteur: data.nom_souscript,
        immatriculation: data.immatriculation,
        pointofsale: data.pos
      }
    });
  }

  // retrieve saved avenant
  getSavedAvenant(numAvenant: string, pos: string = null) {
    return this.http.get(`${environment.automobileExtraBaseUrl}/avenant/get/${numAvenant}`, {params: pos ? {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'), pointofsale: pos} : {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }


  //save avenant
  saveAvenant(data: any, pos: string = null) {
    return this.http.post(`${environment.automobileExtraBaseUrl}/avenant/save-avenant`, data, {params: pos ? {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'), pointofsale: pos} : {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }
  

  // search for latest pending avenant on contract
  getPendingAvenants(data) {
    return this.http.get(`${environment.automobileExtraBaseUrl}/avenant/get-latest-avenant`, {
      params: {
        token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'),
        policynumber: data.policynumber,
        pointofsale: data.pos
      }
    });
  }

  // Initiate payment
  initiatePayment(data: any, pos: string = null) {
    return this.http.post(`${environment.automobileExtraBaseUrl}/policy/initiate-payment`, data, {params: pos ? {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'), pointofsale: pos} : {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }

  // Initiate payment
  initiateAvenantPayment(data: any, pos: string = null) {
    return this.http.post(`${environment.automobileExtraBaseUrl}/avenant/initiate-payment`, data, {params: pos ? {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'), pointofsale: pos} : {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }

  validateMonaOtp(paiementId: string, code: string, pos: string = null) {
    const body = { paiementId, code };
    return this.http.post(`${environment.automobileExtraBaseUrl}/payment/mona/validate-otp`, body, {params: pos ? {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN'), pointofsale: pos} : {token: localStorage.getItem('ACTIVA_REDACTEUR_TOKEN')}});
  }
  
  //print DTA
  printAttestationDTA(idctr, token) {
    return this.http.post(`${environment.printingBaseUrl}/api/v1/GetPdfReportCMR/${idctr}`, {params: {token}});
  }
}